import React from 'react'
import { ControlledModal, ModalTitle } from '../../../layouts/components/modal'
import { useAppContext, User } from '../../../providers/context'
import { useTranslation } from '../../../providers/translation'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import {
  IconCaretDown,
  IconFileUpload,
  IconMicrophone,
  IconPhotoUpload,
  IconWarningCircle,
  IconWarningTriangle,
} from '../../../icons/library'
import { Checkbox, LabeledCheckbox } from '../../../components/form/checkbox'
import { Dropdown, DropdownItem } from '../../../components/simple/dropdown'
import { TextInput } from '../../../components/form/textInput'
import { getNormalTagsForUser, QuillTextarea } from '../../../components/quill/quillTextarea'
import { FamilyTypeSelector, TeacherTypeSelector } from './typeSelector'
import { RecipientInfoSelector } from './recipientInfoSelector'
import { ShareRecipientSelector } from './sharedRecipientSelector'
import { Tooltip } from '../../../components/simple/tooltip'
import { TemplatesRow } from './templatesRow'
import { emptyToUndefined, wrapOptionalSetState } from '../../../utils'
import { Popups } from '../../../providers/popups'
import { preSendChecks, sendComposedMessage, composeStateToPostableMessage, messageSuccessNotifications } from './send'
import { BottomRightResizable } from '../../../layouts/components/resizable'
import { usePreview } from '../../modals/preview'
import { useAskBeforeLeavingPage } from '../../../hooks/hooks'
import { showAxiosErrorAsToast, useRequestsWhileMounted } from '../../../request'
import {
  ComposeState,
  DEFAULT_COMPOSE_STATE,
  SendOption,
  SendOptions,
  SharedRecipient,
  StudentRecipient,
  TeacherRecipient,
} from './composeTypes'
import { RegardingStudentSelector } from './regardingStudentSelector'
import {
  composeStatesDifferent,
  getComposeIndividualsFromIDs,
  singleSelectedRecipient,
  usePreviewState,
  useSaveGroup,
  useUnreachableRecipientsWarning,
} from './composeHelpers'
import { SendButtonPopover } from './composePopups'
import { AudioRecorder } from '../../../components/media/audioRecorder'
import { FileUploadRow, FileUploadRowHandle } from '../../../components/media/fileUpload'
import { useCaller } from '../../../providers/callProvider'
import { Toasts } from '../../../providers/toast'
import { MassRecipientUpload, RecipientSelector } from './recipientSelector'
import Delta from 'quill-delta'
import {
  loadRecipientsImmediately,
  loadSharedRecipientsImmediately,
} from '../../../components/userSelector/userSelectorPlugins'

interface ComposeProps {
  composeState: ComposeState
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
  reloadContacts: () => void
  onSendComplete: (message: ComposeState) => void
  user: User
  preloadedRecipients: (TeacherRecipient | StudentRecipient)[] | undefined
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>
  preloadedSharedRecipients: SharedRecipient[] | undefined
  setPreloadedSharedRecipients: React.Dispatch<React.SetStateAction<SharedRecipient[] | undefined>>
}

export const ComposeModal: React.FC<ComposeProps> = ({
  composeState: rawComposeState,
  setComposeState,
  reloadContacts,
  onSendComplete,
  user,
  preloadedRecipients,
  setPreloadedRecipients,
  preloadedSharedRecipients,
  setPreloadedSharedRecipients,
}) => {
  const { setState: setAppState } = useAppContext()
  const { inCall, makeCall } = useCaller()
  const { tr } = useTranslation()
  const composeState = rawComposeState || DEFAULT_COMPOSE_STATE
  const preloadingRecipients = usePreloadRecipients(
    user,
    preloadedRecipients,
    preloadedSharedRecipients,
    setPreloadedRecipients,
    setPreloadedSharedRecipients
  )
  const initialRecipientsLoading = useInitialRecipients(
    user,
    composeState,
    setComposeState,
    preloadedRecipients,
    preloadedSharedRecipients
  )
  const preMountLoading = preloadingRecipients || initialRecipientsLoading
  const [loadingGroup, setLoadingGroup] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [showLoader, setShowLoader] = React.useState(false)
  const [attachmentsUploading, setAttachmentsUploading] = React.useState(false)
  const [recordingInProgress, setRecordingInProgress] = React.useState(false)
  const [originalComposeState, setOriginalComposeState] = React.useState(composeState)
  React.useEffect(() => {
    if (!preMountLoading) setOriginalComposeState(composeState)
  }, [preMountLoading])
  const isTeacher = user.type === 'teacher'
  const isGuardian = user.type === 'guardian'
  const helpURL = isTeacher
    ? 'https://reachmyteach.zendesk.com/hc/en-us/articles/6418269600665-Adding-a-Message'
    : 'https://reachmyteach.zendesk.com/hc/en-us/articles/6396987133465-Add-a-message'

  const showSubject =
    (!composeState.sendTypes.has('mass') || composeState.sendTypes.size === 2) && composeState.sendTypes.has('email')
  const showBody = composeState.sendTypes.has('mass') || !composeState.sendTypes.has('phone')
  const bothEmailText = composeState.sendTypes.has('email') && composeState.sendTypes.has('text')
  const showAttachments = !composeState.sendTypes.has('phone')
  React.useEffect(() => {
    if (!showAttachments) setComposeState((cs) => cs && { ...cs, attachments: [] })
  }, [showAttachments])
  const limitRecipients = composeState.sendTypes.size === 1 && composeState.sendTypes.has('phone')
  const showRecording = composeState.sendTypes.has('phone') && composeState.sendTypes.has('mass')
  React.useEffect(() => {
    if (!showRecording) setComposeState((cs) => cs && { ...cs, recording: undefined })
  }, [showRecording])
  const availableSendOptions = useAvailableSendOptions(composeState, setComposeState, user)
  const previewState = usePreviewState(isTeacher, composeState)
  const unreachableRecipientsWarning = useUnreachableRecipientsWarning(isTeacher, composeState)
  const saveGroup = useSaveGroup(
    setLoadingGroup,
    composeState,
    setComposeState,
    setPreloadedRecipients,
    setAppState,
    user,
    reloadContacts
  )
  const tags = React.useMemo(() => getNormalTagsForUser(user), [user])
  const { canSend, canSendRef } = useCanSend(
    composeState,
    user,
    attachmentsUploading,
    inCall,
    recordingInProgress,
    showSubject,
    showBody
  )

  const [sendButtonHovered, setSendButtonHovered] = React.useState(false)
  const highlightReasonsWeCantSend = sendButtonHovered && !canSend

  useAskBeforeLeavingPage(
    composeState.selectedRecipients.length > 0 && composeState.bodyHTML !== DEFAULT_COMPOSE_STATE.bodyHTML
  )

  const { showMessagePreview, showLatestMessagePreview } = usePreview()
  const previewMessage = React.useCallback(
    async (message: ComposeState) => {
      setLoading(true)
      await showMessagePreview(composeStateToPostableMessage(message, true))
      setLoading(false)
    },
    [setLoading]
  )

  const immediatelySendAndClose = React.useCallback(
    (message: ComposeState) => {
      setLoading(true)
      setShowLoader(true)
      if (message.sendOption.type === 'call') {
        const { studentID, guardianID } = message.recipientsInfo.individuals[0]
        const { name } = singleSelectedRecipient(message)!
        // we dont bother waiting for the call to finish
        makeCall({ studentID, guardianID, name }, () => {
          // this callback is when the call successfully starts
          setComposeState(undefined)
        })
          .catch((e) => {
            console.error(e)
            Toasts.showToast({ message: 'Error making call', error: true })
          })
          .finally(() => {
            setLoading(false)
            setShowLoader(false)
          })
      } else {
        sendComposedMessage(message)
          .then((response) => {
            setComposeState(undefined)
            messageSuccessNotifications(user, message, response, showLatestMessagePreview)
            onSendComplete(message)
          })
          .catch(showAxiosErrorAsToast)
          .finally(() => {
            setLoading(false)
            setShowLoader(false)
          })
      }
    },
    [setComposeState, setLoading]
  )

  const sendButtonRef = React.useRef<HTMLButtonElement>(null)

  const [showSendLaterCalendar, setShowSendLaterCalendar] = React.useState(false)
  const [showSchedulingCalendar, setShowSchedulingCalendar] = React.useState(false)
  const [showTestMessagePopup, setShowTestMessagePopup] = React.useState(false)

  const filterVisibleComposeState = (composeState: ComposeState) => {
    return {
      ...composeState,
      subject: showSubject ? composeState.subject : '',
      bodyHTML: showBody ? composeState.bodyHTML : '',
      bodyDelta: showBody ? composeState.bodyDelta : new Delta(),
    }
  }

  const sendMessage = async (composeState: ComposeState) => {
    setLoading(true)
    const processedComposeState = await preSendChecks(filterVisibleComposeState(composeState), previewState, user)
    if (processedComposeState) immediatelySendAndClose(processedComposeState)
    else setLoading(false)
  }

  const sendButtonPopups = () => {
    if (composeState.sendOption.type === 'later') setShowSendLaterCalendar(true)
    else if (
      composeState.sendOption.type === 'schedule-interpreted' ||
      composeState.sendOption.type === 'schedule-video'
    )
      setShowSchedulingCalendar(true)
    else if (composeState.sendOption.type === 'test') setShowTestMessagePopup(true)
    else return false
    return true
  }

  const sendButtonClick = () => {
    if (!sendButtonPopups()) sendMessage(composeState)
  }

  React.useEffect(() => {
    if (canSendRef.current) {
      sendButtonRef.current?.focus()
      setTimeout(() => sendButtonPopups())
    }
  }, [composeState.sendOption.type])

  const fileUploadHandleRef = React.useRef<FileUploadRowHandle>(null)

  return (
    <ControlledModal
      help={user.faria ? undefined : helpURL}
      id="tutorial_composeModal"
      open={true}
      noAutoFocus
      onOpenChange={() => {
        if (
          canSend &&
          composeStatesDifferent(composeState, originalComposeState) &&
          !composeState.sendOption.forPhone &&
          composeState.sendOption.type !== 'test'
        )
          Popups.confirm({
            title: 'Do you want to save this message as a draft?',
            yesText: 'Save Draft',
            noText: 'Discard',
            cancel: 'Cancel',
          }).then((confirmed) => {
            if (confirmed) immediatelySendAndClose({ ...composeState, sendOption: { type: 'draft', name: '' } })
            if (confirmed === false) setComposeState(undefined)
          })
        else setComposeState(undefined)
      }}
      size="xl"
      disabled={loading}
      showLoader={preMountLoading || showLoader}
      className={preMountLoading ? 'min-h-[393px]' : ''}
      dropTarget={{
        canDrop: () => !!fileUploadHandleRef.current,
        onDrop: (files) => {
          fileUploadHandleRef.current?.upload(files)
        },
      }}
    >
      {!preMountLoading && (
        <>
          <ModalTitle hidden={isTeacher} className="mb-1 text-xl font-bold">
            {tr('New Message')}
          </ModalTitle>
          {isTeacher && (
            <TeacherTypeSelector
              sendTypes={composeState.sendTypes}
              setSendTypes={wrapOptionalSetState(setComposeState, 'sendTypes')}
              user={user}
              massAllowReplying={composeState.massAllowReplying}
              setMassAllowReplying={wrapOptionalSetState(setComposeState, 'massAllowReplying')}
            />
          )}
          {isGuardian && (
            <RegardingStudentSelector
              user={user}
              regardingStudent={composeState.regardingStudent}
              setRegardingStudent={wrapOptionalSetState(setComposeState, 'regardingStudent')}
            />
          )}
          <div className={`mt-2 flex items-center gap-2 ${isTeacher ? '' : 'mb-2'}`}>
            <RecipientSelector
              limitRecipients={limitRecipients}
              highlightReasonsWeCantSend={highlightReasonsWeCantSend}
              composeState={composeState}
              setSelectedRecipients={wrapOptionalSetState(setComposeState, 'selectedRecipients')}
              user={user}
              loadedUsers={preloadedRecipients || []}
            />
            {composeState.sendTypes.has('mass') && (
              <MassRecipientUpload
                setLoading={setLoading}
                setSelectedRecipients={wrapOptionalSetState(setComposeState, 'selectedRecipients')}
              />
            )}
            {isTeacher && !user.externalStaff && !loadingGroup && (
              <Button
                tooltip="Save these recipients as a group"
                className="self-start"
                disabled={composeState.selectedRecipients.length < 2}
                onClick={saveGroup}
              >
                Group
              </Button>
            )}
          </div>
          {isTeacher && (
            <>
              <RecipientInfoSelector
                composeState={composeState}
                setRecipientsInfo={wrapOptionalSetState(setComposeState, 'recipientsInfo')}
                limitRecipients={limitRecipients}
                showExtraInviteSubbuttons={!!composeState.sendOption.allowExtraInvites}
                user={user}
                highlightReasonsWeCantSend={highlightReasonsWeCantSend}
              />
              {composeState.recipientsInfo.toSharedTeachers && (
                <>
                  <ShareRecipientSelector
                    composeState={composeState}
                    loadedRecipients={preloadedSharedRecipients || []}
                    setRecipientsInfo={wrapOptionalSetState(setComposeState, 'recipientsInfo')}
                    highlightReasonsWeCantSend={highlightReasonsWeCantSend}
                  />
                  {!composeState.sendTypes.has('mass') && (
                    <Tooltip text="If checked, shared teachers will see replies from anyone on the message. When unchecked, shared teachers will only see messages sent to them.">
                      <div className="ml-2 mr-auto flex items-center gap-2">
                        Enable shared teachers to see all replies
                        <Checkbox
                          charcoal
                          checked={composeState.sharedTeachersSeeReplies}
                          onChecked={wrapOptionalSetState(setComposeState, 'sharedTeachersSeeReplies')}
                        />
                      </div>
                    </Tooltip>
                  )}
                </>
              )}
              {showBody && (
                <TemplatesRow composeState={composeState} setComposeState={setComposeState} showSubject={showSubject} />
              )}
              {showRecording && (
                <div className="flex items-center gap-2">
                  <AudioRecorder
                    recording={composeState.recording}
                    setRecording={wrapOptionalSetState(setComposeState, 'recording')}
                    setRecorderBusy={setRecordingInProgress}
                    tooltip={
                      'This recording will only be used for untranslated phone calls. For recordings under 60 seconds, we automatically transcribe your recording to help write your message to be sent by other methods or translated.'
                    }
                    extractTranscript={(text, overwrite) => {
                      if (overwrite || composeState.bodyHTML === DEFAULT_COMPOSE_STATE.bodyHTML)
                        setComposeState((cs) => cs && { ...cs, bodyHTML: text })
                    }}
                    className={`transition-colors duration-150 ${recordingInProgress && highlightReasonsWeCantSend ? 'border border-red-500' : ''}`}
                  >
                    <IconMicrophone className="mr-1" />
                    Add Voice Recording For Phone Calls
                  </AudioRecorder>
                </div>
              )}
            </>
          )}
          {showBody && (
            <BottomRightResizable
              minHeight={200}
              minWidth={'initial'}
              maxWidth={'90vw'}
              maxHeight={'50vh'}
              className={`flex gap-2 sm:max-h-[50vh] -sm:flex-col`}
              resizeScaleX={2} // because this modal is centered
            >
              <div className="flex min-h-0 min-w-0 flex-grow flex-col sm:basis-[1px]" id="tutorial_messageBody">
                {showSubject && (
                  <TextInput
                    autocomplete="compose_subject"
                    className={`-mb-px rounded-b-none rounded-t-lg px-2 pb-[.2rem] pt-[.35rem] placeholder:text-[13px] ${bothEmailText && user.type === 'teacher' ? 'border-warning' : ''} ${highlightReasonsWeCantSend && composeState.subject.length === 0 ? '!border-red-500' : ''}`}
                    placeholder={tr('Subject')}
                    value={composeState.subject}
                    onChange={wrapOptionalSetState(setComposeState, 'subject')}
                    rightIcons={
                      bothEmailText &&
                      user.type === 'teacher' && (
                        <IconWarningCircle
                          className="mt-px text-warning"
                          tooltip="This subject is not included in text messages. Make sure not to put critical information in the subject without also including it in the message below."
                        />
                      )
                    }
                  />
                )}
                <QuillTextarea
                  className={`rounded-b-lg ${showSubject ? '' : 'rounded-t-lg'}`}
                  valueHTML={composeState.bodyHTML}
                  valueDelta={composeState.bodyDelta}
                  onValueChange={(html, delta) =>
                    setComposeState({ ...composeState, bodyDelta: delta, bodyHTML: html })
                  }
                  infoForUser={user}
                  caption={tr('select text for formatting options')}
                  formats={['bold', 'italic', 'list', 'link']}
                  flagPoorTranslations
                  tags={tags}
                  invalid={highlightReasonsWeCantSend && composeState.bodyHTML === DEFAULT_COMPOSE_STATE.bodyHTML}
                  imageHandler={(file) => {
                    if (fileUploadHandleRef.current) fileUploadHandleRef.current.upload([file])
                  }}
                  warnIfLong={composeState.sendTypes.has('text')}
                />
              </div>
              {composeState.aiSuggestion && (
                <QuillTextarea
                  className={`rounded-lg border !border-blue-500 text-[13px] sm:basis-[1px] ${showSubject ? 'sm:mt-8' : ''}`}
                  valueHTML={composeState.aiSuggestion}
                  onValueChange={(html) => setComposeState({ ...composeState, aiSuggestion: html })}
                  caption="AI Suggestion"
                  formats={['bold', 'italic', 'list', 'link']}
                  flagPoorTranslations
                  tags={tags}
                  invalid={highlightReasonsWeCantSend}
                />
              )}
            </BottomRightResizable>
          )}
          {!isTeacher && (
            <FamilyTypeSelector
              user={user}
              sendTypes={composeState.sendTypes}
              setSendTypes={wrapOptionalSetState(setComposeState, 'sendTypes')}
            />
          )}
          {composeState.sendTypes.has('mass') && (
            <LabeledCheckbox
              className="self-start"
              label="Emergency Broadcast"
              tooltip="This message will ignore recipient's notification settings."
              checked={!!composeState.emergency}
              onChecked={wrapOptionalSetState(setComposeState, 'emergency')}
            />
          )}
          <div className="mt-2 flex gap-3">
            {showAttachments && (
              <FileUploadRow
                ref={fileUploadHandleRef}
                setUploadedFiles={wrapOptionalSetState(setComposeState, 'attachments')}
                uploadedFiles={composeState.attachments}
                onUploading={setAttachmentsUploading}
                highlightInProgressUploads={highlightReasonsWeCantSend}
              >
                <Button buttonStyle={ButtonStyle.ghostLight}>
                  <IconFileUpload className="-sm:hidden" />
                  <IconPhotoUpload className="sm:hidden" />
                </Button>
              </FileUploadRow>
            )}
            {composeState.sendOption.forLexikeet && (
              <LabeledCheckbox
                className="ml-4"
                label="In Person"
                tooltip="Check this if the recipient is with you in person, so we do not send them a message about this interpreted call."
                checked={!!composeState.lexikeetInPerson}
                onChecked={wrapOptionalSetState(setComposeState, 'lexikeetInPerson')}
              />
            )}

            <div className="ml-auto flex items-center gap-3">
              {unreachableRecipientsWarning && (
                <Tooltip text={<div dangerouslySetInnerHTML={{ __html: unreachableRecipientsWarning }} />}>
                  <IconWarningTriangle className="text-warning" size="xl" />
                </Tooltip>
              )}
              {previewState && (
                <div
                  className={`${previewState.count !== 0 ? 'cursor-pointer hover:underline' : ''} px-2 font-semibold text-logo`}
                  onClick={() => previewState.count !== 0 && previewMessage(filterVisibleComposeState(composeState))}
                  id="tutorial_preview"
                >
                  Preview{previewState.count !== undefined ? ` to ${previewState.count}` : ''}
                </div>
              )}
              <div className="flex">
                <SendButtonPopover
                  composeState={composeState}
                  setComposeState={setComposeState}
                  showSendLaterCalendar={showSendLaterCalendar}
                  showSchedulingCalendar={showSchedulingCalendar}
                  showTestMessagePopup={showTestMessagePopup}
                  setShowSendLaterCalendar={setShowSendLaterCalendar}
                  setShowSchedulingCalendar={setShowSchedulingCalendar}
                  setShowTestMessagePopup={setShowTestMessagePopup}
                  sendMessage={sendMessage}
                >
                  <Button
                    id="tutorial_send"
                    logo
                    shape={ButtonShape.groupStart}
                    disabled={!canSend}
                    onClick={sendButtonClick}
                    ref={sendButtonRef}
                    onHoverChange={setSendButtonHovered}
                  >
                    {tr(composeState.sendOption.name)}
                  </Button>
                </SendButtonPopover>
                <Dropdown
                  offset={5}
                  button={
                    <Button logo shape={ButtonShape.groupEnd} className="!pl-2 !pr-3">
                      <IconCaretDown />
                    </Button>
                  }
                >
                  {availableSendOptions.map((option) => (
                    <SendOptionItem
                      key={option.type}
                      option={option}
                      composeState={composeState}
                      setComposeState={setComposeState}
                    />
                  ))}
                </Dropdown>
              </div>
            </div>
          </div>
        </>
      )}
    </ControlledModal>
  )
}

const SendOptionItem: React.FC<{
  option: SendOption
  composeState: ComposeState
  setComposeState: (state: ComposeState) => void
}> = ({ option, composeState, setComposeState }) => {
  const { tr } = useTranslation()
  const item = (
    <DropdownItem onClick={() => setComposeState({ ...composeState, sendOption: option })}>
      {tr(option.name)}
    </DropdownItem>
  )
  return option.tooltip ? <Tooltip text={option.tooltip}>{item}</Tooltip> : item
}

const useAvailableSendOptions = (
  composeState: ComposeState,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>,
  user: User
) => {
  const availableSendOptions = React.useMemo(() => {
    let singleLKRecipient = !!singleSelectedRecipient(composeState)?.lk

    return [...SendOptions].filter(
      (o) =>
        (o.forUser === undefined || o.forUser === user.type) &&
        !!o.forPhone === (composeState.sendTypes.has('phone') && composeState.sendTypes.size === 1) &&
        !(o.sendTypesDoesntHave && composeState.sendTypes.has(o.sendTypesDoesntHave)) &&
        !(o.forLexikeet && !singleLKRecipient)
    )
  }, [user.type, composeState.sendTypes, composeState.recipientsInfo.individuals])

  React.useEffect(() => {
    if (!availableSendOptions.includes(composeState.sendOption)) {
      setComposeState((cs) => cs && { ...cs, sendOption: availableSendOptions[0] })
    }
  }, [availableSendOptions, composeState.sendOption])

  // Auto switch every time availableOptions changes
  React.useEffect(() => {
    setComposeState((cs) => {
      if (!cs) return cs
      const optionToAutoSelect = availableSendOptions.find((o) => o.shouldAutoSwitchTo?.(cs))
      return optionToAutoSelect ? { ...cs, sendOption: optionToAutoSelect } : cs
    })
  }, [availableSendOptions])

  return availableSendOptions
}

const useCanSend = (
  composeState: ComposeState,
  user: User,
  attachmentsUploading: boolean,
  inCall: boolean,
  recordingInProgress: boolean,
  showSubject: boolean,
  showBody: boolean
) => {
  const canSend = React.useMemo(() => {
    if (attachmentsUploading || recordingInProgress) return false
    if (composeState.aiSuggestion !== undefined) return false
    if (showSubject && composeState.subject.length === 0) return false
    if (showBody && composeState.bodyHTML === DEFAULT_COMPOSE_STATE.bodyHTML) return false

    if (composeState.sendTypes.size === 0) return false
    if (composeState.sendOption.type === 'test') return true

    if (composeState.selectedRecipients.length === 0) return false
    if (
      user.type === 'teacher' &&
      composeState.recipientsInfo.individuals.length === 0 &&
      !composeState.recipientsInfo.toGuardians &&
      !composeState.recipientsInfo.toStudents &&
      !composeState.recipientsInfo.toSharedTeachers
    )
      return false
    if (
      composeState.recipientsInfo.toSharedTeachers &&
      composeState.recipientsInfo.sharedTeacherRecipients.length === 0
    )
      return false
    if (inCall && composeState.sendOption.type === 'call') return false

    if (composeState.recipientsInfo.individuals.length === 0 && composeState.sendOption.requiresIndividualRecipient)
      return false

    return true
  }, [composeState, user, attachmentsUploading, inCall, recordingInProgress, showSubject, showBody])
  // keep this ref of canSend so that you can access the latest value in functions without canSend as a dependency
  const canSendRef = React.useRef(canSend)
  canSendRef.current = canSend
  return { canSend, canSendRef }
}

const usePreloadRecipients = (
  user: User,
  preloadedRecipients: (TeacherRecipient | StudentRecipient)[] | undefined,
  preloadedSharedRecipients: SharedRecipient[] | undefined,
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>,
  setPreloadedSharedRecipients: React.Dispatch<React.SetStateAction<SharedRecipient[] | undefined>>
) => {
  const makeRequest = useRequestsWhileMounted()
  React.useEffect(() => {
    if (preloadedRecipients === undefined) {
      makeRequest<(TeacherRecipient | StudentRecipient)[]>('/frontend_api/getPreloadedRecipients')
        .then((newPreloadedRecipients) => {
          setPreloadedRecipients(newPreloadedRecipients)
        })
        .catch(showAxiosErrorAsToast)
    }

    if (user.type === 'teacher' && preloadedSharedRecipients === undefined) {
      makeRequest<SharedRecipient[]>('/frontend_api/getPreloadedSharedRecipients')
        .then((newPreloadedSharedRecipients) => {
          setPreloadedSharedRecipients(newPreloadedSharedRecipients)
        })
        .catch(showAxiosErrorAsToast)
    }
  }, [])
  return preloadedRecipients === undefined || (user.type === 'teacher' && preloadedSharedRecipients === undefined)
}

const useInitialRecipients = (
  user: User,
  composeState: ComposeState,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>,
  preloadedRecipients: (TeacherRecipient | StudentRecipient)[] | undefined,
  preloadedSharedRecipients: SharedRecipient[] | undefined
) => {
  const loadingRecipients = React.useRef(false)
  const loadingShared = React.useRef(false)
  React.useEffect(() => {
    if (
      !loadingRecipients.current &&
      preloadedRecipients &&
      (composeState.initiallySelectedRecipients?.users || composeState.initiallySelectedRecipients?.groups)
    ) {
      loadingRecipients.current = true
      loadRecipientsImmediately(
        preloadedRecipients,
        composeState.initiallySelectedRecipients.users ?? [],
        composeState.initiallySelectedRecipients.groups ?? []
      ).then((recipients) => {
        setComposeState(
          (cs) =>
            cs && {
              ...cs,
              selectedRecipients: recipients,
              recipientsInfo: {
                ...cs.recipientsInfo,
                individuals: getComposeIndividualsFromIDs(
                  user,
                  recipients,
                  composeState.initiallySelectedRecipients?.individuals
                ),
              },
              initiallySelectedRecipients: emptyToUndefined({
                ...cs.initiallySelectedRecipients,
                users: undefined,
                groups: undefined,
                individuals: undefined,
              }),
            }
        )
      })
    }
    if (!loadingShared.current && preloadedSharedRecipients && composeState.initiallySelectedRecipients?.shared) {
      loadingShared.current = true
      loadSharedRecipientsImmediately(preloadedSharedRecipients, composeState.initiallySelectedRecipients.shared).then(
        (sharedRecipients) => {
          setComposeState(
            (cs) =>
              cs && {
                ...cs,
                recipientsInfo: {
                  ...cs.recipientsInfo,
                  sharedTeacherRecipients: sharedRecipients,
                },
                initiallySelectedRecipients: emptyToUndefined({
                  ...cs.initiallySelectedRecipients,
                  shared: undefined,
                }),
              }
          )
        }
      )
    }
  }),
    [user, composeState, preloadedRecipients, preloadedSharedRecipients]

  return composeState.initiallySelectedRecipients !== undefined
}
